import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useNotifications } from '../hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';

const { FiBell, FiCheck, FiInfo, FiAlertCircle, FiClock, FiChevronRight } = FiIcons;

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, loading } = useNotifications();

  const getIcon = (type) => {
    switch (type) {
      case 'reminder': return { icon: FiClock, color: 'text-blue-500 bg-blue-50' };
      case 'achievement': return { icon: FiCheck, color: 'text-green-500 bg-green-50' };
      default: return { icon: FiInfo, color: 'text-slate-500 bg-slate-50' };
    }
  };

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-400 hover:text-gray-600 relative transition-colors"
      >
        <SafeIcon icon={FiBell} className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full border-2 border-white flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 mt-2 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
            >
              <div className="px-5 py-4 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-bold text-gray-900">Notifications</h3>
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest bg-blue-50 px-2 py-0.5 rounded">
                  {unreadCount} New
                </span>
              </div>

              <div className="max-h-[400px] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-gray-400 text-sm italic">Loading...</div>
                ) : notifications.length > 0 ? (
                  notifications.map((n) => {
                    const style = getIcon(n.type);
                    return (
                      <div 
                        key={n.id}
                        onClick={() => {
                          markAsRead(n.id);
                          if (!n.link) setIsOpen(false);
                        }}
                        className={`p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer relative ${!n.is_read ? 'bg-blue-50/30' : ''}`}
                      >
                        <div className="flex gap-4">
                          <div className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${style.color}`}>
                            <SafeIcon icon={style.icon} className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900 mb-0.5">{n.title}</p>
                            <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{n.message}</p>
                            <p className="text-[10px] text-gray-400 mt-2 font-medium">
                              {formatDistanceToNow(new Date(n.created_at))} ago
                            </p>
                          </div>
                          {n.link && (
                            <Link 
                              to={n.link} 
                              className="self-center p-1 text-gray-300 hover:text-blue-600"
                              onClick={() => setIsOpen(false)}
                            >
                              <SafeIcon icon={FiChevronRight} />
                            </Link>
                          )}
                        </div>
                        {!n.is_read && (
                          <div className="absolute top-4 right-4 w-2 h-2 bg-blue-600 rounded-full"></div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="p-12 text-center">
                    <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                      <SafeIcon icon={FiBell} className="text-gray-300" />
                    </div>
                    <p className="text-sm text-gray-500 font-medium">No notifications yet</p>
                  </div>
                )}
              </div>

              <div className="p-3 bg-gray-50 text-center border-t border-gray-100">
                <button className="text-xs font-bold text-gray-500 hover:text-blue-600 transition-colors">
                  View All Activity
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;